/**
 * PTEL Tool - AuthModal
 * Modal contenedor para flujos de autenticación
 *
 * @module components/auth/AuthModal
 * @version 2.1.0
 * @changes
 *   2.1.0 — Fix iconos pipeline: viewBox 0 0 24 24 + rutas Heroicons correctas.
 *            Elimina referencia a términos de uso (no existe página pública).
 */

import type { CSSProperties } from 'react';
import { useState, useEffect, useCallback } from 'react';

import type { AuthModalProps, AuthView } from './auth.types';
import { getAuthTokens } from './auth.tokens';
import { LoginForm } from './LoginForm';
import { RegisterForm } from './RegisterForm';
import { PasswordResetForm } from './PasswordResetForm';

// ── 4 pasos del pipeline con iconos Heroicons 24px correctos ──────────────
const PIPELINE_STEPS = [
  {
    label: 'Normaliza',
    desc: 'Detecta y corrige 62 formatos y corrupción UTF-8',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
        style={{ width: 18, height: 18, flexShrink: 0 }}>
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
      </svg>
    ),
  },
  {
    label: 'Valida',
    desc: 'Geocodifica con APIs oficiales y scoring 0–100',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
        style={{ width: 18, height: 18, flexShrink: 0 }}>
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
  },
  {
    label: 'Coteja',
    desc: 'Contrasta con catálogo DERA (~166 000 elementos)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
        style={{ width: 18, height: 18, flexShrink: 0 }}>
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6.75v6.75" />
      </svg>
    ),
  },
  {
    label: 'Exporta',
    desc: 'GeoJSON, Excel, Shapefile y más en EPSG:25830',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
        style={{ width: 18, height: 18, flexShrink: 0 }}>
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
      </svg>
    ),
  },
] as const;

export function AuthModal({
  theme,
  initialView = 'register',
  isOpen,
  onClose,
  onLogin,
  onRegister,
  onPasswordReset,
  onResendVerification,
  isLoading = false,
  error,
  onClearError,
  onLoginSuccess,
}: AuthModalProps) {
  const t = getAuthTokens(theme);
  const [view, setView] = useState<AuthView>(initialView);

  useEffect(() => {
    if (!isOpen) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset de vista al abrir modal; guarda explícita (!isOpen)
    setView(initialView);
  }, [isOpen, initialView]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isLoading) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, isLoading, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const navigate = useCallback((newView: AuthView) => {
    if (onClearError) onClearError();
    setView(newView);
  }, [onClearError]);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) onClose();
  }, [isLoading, onClose]);

  const handleLogin = useCallback(async (email: string, password: string) => {
    await onLogin(email, password);
    if (onLoginSuccess) onLoginSuccess();
  }, [onLogin, onLoginSuccess]);

  if (!isOpen) return null;

  const showTabs = view === 'login' || view === 'register';
  const bgInactive = theme === 'dark' ? 'rgba(0,0,0,0.28)' : 'rgba(0,0,0,0.05)';
  const bgActive   = t.bgCard;

  const injectedCSS = `
    @keyframes authModalFadeIn {
      from { opacity: 0; } to { opacity: 1; }
    }
    @keyframes authModalZoomIn {
      from { opacity: 0; transform: scale(0.96) translateY(8px); }
      to   { opacity: 1; transform: scale(1) translateY(0); }
    }
    .auth-tab {
      flex: 1;
      padding: 14px 12px;
      border: none;
      font-size: 14px;
      font-weight: 600;
      font-family: 'Space Grotesk', sans-serif;
      letter-spacing: -0.01em;
      cursor: pointer;
      user-select: none;
      transition: opacity 0.15s, color 0.15s;
    }
    .auth-tab:hover { opacity: 0.82; }
    .auth-tab--inactive { background: ${bgInactive}; color: ${t.textMuted}; }
    .auth-tab--active   { background: ${bgActive};   color: ${t.primary};   }
    .auth-close-btn:hover {
      border-color: ${t.textMuted} !important;
      color: ${t.text} !important;
    }
  `;

  // ── Estilos ──────────────────────────────────────────────────────────────────
  const backdropStyle: CSSProperties = {
    position: 'fixed', inset: 0, zIndex: 50,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '16px',
    background: t.backdrop,
    backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
    animation: 'authModalFadeIn 0.2s ease-out',
  };
  const clipLayerStyle: CSSProperties = {
    position: 'relative', width: '100%', maxWidth: '460px',
    background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: '14px',
    boxShadow: theme === 'dark' ? '0 8px 40px rgba(0,0,0,0.55)' : '0 8px 40px rgba(0,0,0,0.13)',
    overflow: 'hidden', animation: 'authModalZoomIn 0.2s ease-out',
  };
  const scrollLayerStyle: CSSProperties = { maxHeight: '90vh', overflowY: 'auto' };
  const tabBarStyle: CSSProperties = {
    display: 'flex', position: 'relative', borderBottom: `1px solid ${t.border}`,
  };
  const tabIndicatorStyle: CSSProperties = {
    position: 'absolute', bottom: 0, left: 0, width: '50%', height: '2px',
    background: t.primary,
    transform: view === 'login' ? 'translateX(0%)' : 'translateX(100%)',
    transition: 'transform 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
    pointerEvents: 'none',
  };
  const closeBtnStyle: CSSProperties = {
    position: 'absolute', top: showTabs ? '10px' : '14px', right: '14px', zIndex: 10,
    width: '28px', height: '28px', background: 'transparent',
    border: `1px solid ${t.border}`, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: t.textMuted, cursor: isLoading ? 'not-allowed' : 'pointer',
    padding: 0, fontSize: '16px', lineHeight: 1,
    transition: 'border-color 0.15s, color 0.15s',
  };
  const gridOverlayStyle: CSSProperties = { display: 'grid' };
  const panelStyle = (active: boolean): CSSProperties => ({
    gridArea: '1 / 1', opacity: active ? 1 : 0,
    transition: 'opacity 0.15s ease',
    pointerEvents: active ? 'auto' : 'none',
    visibility: active ? 'visible' : 'hidden',
    display: 'flex', flexDirection: 'column',
  });

  // ── Pipeline al pie del panel login ─────────────────────────────────────────
  const pipelineWrapStyle: CSSProperties = {
    marginTop: 'auto', paddingTop: '20px',
    borderTop: `1px solid ${t.border}`,
    display: 'flex', flexDirection: 'column', gap: '8px',
  };
  const pipelineHeadStyle: CSSProperties = {
    fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em',
    textTransform: 'uppercase', color: t.textMuted, marginBottom: '4px',
  };
  const pipelineItemStyle: CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '8px 10px',
    background: theme === 'dark' ? 'rgba(45,212,191,0.04)' : 'rgba(13,148,136,0.04)',
    border: `1px solid ${theme === 'dark' ? 'rgba(45,212,191,0.1)' : 'rgba(13,148,136,0.1)'}`,
    borderRadius: '8px',
  };
  const pipelineIconWrapStyle: CSSProperties = {
    color: t.primary, display: 'flex', alignItems: 'center', flexShrink: 0,
  };
  const pipelineLabelStyle: CSSProperties = {
    fontSize: '13px', fontWeight: 700, color: t.text,
    fontFamily: "'Space Grotesk', sans-serif", minWidth: '72px',
  };
  const pipelineDescStyle: CSSProperties = {
    fontSize: '11px', color: t.textMuted, lineHeight: 1.3,
  };

  return (
    <div
      style={backdropStyle}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
    >
      <style>{injectedCSS}</style>

      <div style={clipLayerStyle}>
        {onClose && (
          <button type="button" className="auth-close-btn"
            onClick={!isLoading ? onClose : undefined}
            aria-label="Cerrar" style={closeBtnStyle}>
            ×
          </button>
        )}

        <div style={scrollLayerStyle}>
          {showTabs && (
            <div style={tabBarStyle} role="tablist" aria-label="Modo de acceso">
              <div role="tab" aria-selected={view === 'login'} tabIndex={0}
                className={`auth-tab ${view === 'login' ? 'auth-tab--active' : 'auth-tab--inactive'}`}
                onClick={() => !isLoading && navigate('login')}
                onKeyDown={(e) => e.key === 'Enter' && !isLoading && navigate('login')}>
                Iniciar sesión
              </div>
              <div role="tab" aria-selected={view === 'register'} tabIndex={0}
                className={`auth-tab ${view === 'register' ? 'auth-tab--active' : 'auth-tab--inactive'}`}
                onClick={() => !isLoading && navigate('register')}
                onKeyDown={(e) => e.key === 'Enter' && !isLoading && navigate('register')}>
                Crear cuenta
              </div>
              <div style={tabIndicatorStyle} aria-hidden="true" />
            </div>
          )}

          <div style={{ padding: '24px' }}>
            {showTabs ? (
              <div style={gridOverlayStyle}>

                {/* Panel LOGIN */}
                <div style={panelStyle(view === 'login')} aria-hidden={view !== 'login'}>
                  <LoginForm
                    theme={theme}
                    onSubmit={handleLogin}
                    onResendVerification={onResendVerification}
                    onNavigateToRegister={() => navigate('register')}
                    onNavigateToPasswordReset={() => navigate('password-reset')}
                    isLoading={isLoading && view === 'login'}
                    externalError={view === 'login' ? error : undefined}
                    onClearError={onClearError}
                    hideTitle hideCard
                  />
                  {/* 4 pasos del pipeline */}
                  <div style={pipelineWrapStyle}>
                    <p style={pipelineHeadStyle}>Qué puedes hacer</p>
                    {PIPELINE_STEPS.map((step) => (
                      <div key={step.label} style={pipelineItemStyle}>
                        <span style={pipelineIconWrapStyle}>{step.icon}</span>
                        <span style={pipelineLabelStyle}>{step.label}</span>
                        <span style={pipelineDescStyle}>{step.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Panel REGISTER */}
                <div style={panelStyle(view === 'register')} aria-hidden={view !== 'register'}>
                  <RegisterForm
                    theme={theme}
                    onSubmit={onRegister}
                    onNavigateToLogin={() => navigate('login')}
                    isLoading={isLoading && view === 'register'}
                    externalError={view === 'register' ? error : undefined}
                    onClearError={onClearError}
                    hideTitle hideCard
                  />
                </div>

              </div>
            ) : (
              <PasswordResetForm
                theme={theme}
                onSubmit={onPasswordReset}
                onNavigateToLogin={() => navigate('login')}
                isLoading={isLoading}
                externalError={error}
                onClearError={onClearError}
                hideCard
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthModal;
